import { TabulationUtils } from 'Editor/services/_Common/TabulationUtils';
import { merge, cloneDeep, isEqual } from 'lodash-es';
import { BaseTypedEmitter } from '_common/services/Realtime';

export const DEFAULT_STYLE_OBJECT: { [index: string]: Editor.Data.Structure.DocumentStyleData } =
  Object.freeze({
    p: {
      p: {
        a: 'l',
        bg: false,
        color: 'rgb(0, 0, 0)',
        fontfamily: 'Times New Roman',
        fontsize: 10,
        italic: false,
        lh: 1,
        lr: 'auto',
        sa: 0,
        sb: 0,
        underline: false,
        bold: false,
        v: false,
        asb: false,
        asa: false,
        wc: true,
        kn: false,
        kl: false,
        cts: false,
        pbb: false,
        // indentation can't exist by default because overrides outline lists with 0
        // ind: {
        //   l: 0,
        //   r: 0,
        // },
      },
      n: 'Paragraph',
      id: 'p',
      b: true,
      a: '',
      e: '',
    },
  });

export type MergedStyleData = Editor.Data.Structure.DocumentStyleData & {
  originalP: Editor.Data.Structure.DocumentStyleProperties;
  extendedP: Editor.Data.Structure.DocumentStyleProperties;
  templateOnly: boolean;
};

export default class DocumentStyle extends BaseTypedEmitter<{
  READY: () => void;
  LOADED: (data: Partial<Editor.Data.Structure.DocumentStyleData> | null) => void;
  UPDATED: (data: Partial<Editor.Data.Structure.DocumentStyleData> | null) => void;
}> {
  id: string;
  private templateStyle: Partial<Editor.Data.Structure.DocumentStyleData> | null = null;
  private mergedStyle: Partial<MergedStyleData> = {};
  private documentStyle: Partial<Editor.Data.Structure.DocumentStyleData> | null = null;
  private parentProperties: Editor.Data.Structure.DocumentStyleProperties = {};
  children: DocumentStyle[] = [];

  static DATA_MAPPER: { [index: string]: (...args: any[]) => void } = {
    // name
    n: (
      result: Partial<Editor.Data.Structure.DocumentStyleData> = {},
      originalData: Editor.Data.Structure.DocumentStyleData,
      newData: Editor.Data.Structure.DocumentStyleData,
    ) => {
      if (originalData.n !== newData?.n && newData?.n != null) {
        result.n = newData.n;
        const _name = `${newData.n}${newData.a ? `;${newData.a}` : ''}`;

        const rawElements = _name.split(new RegExp('[,;]', 'g')).map((element) => element.trim());
        if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 't'].includes(newData.id)) {
          if (rawElements[0] === newData.n) {
            result.a = rawElements.slice(1, rawElements.length).join(',');
          } else {
            result.a = rawElements.join(',');
          }
        } else {
          result.a = rawElements.slice(1, rawElements.length).join(',');
        }
      } else {
        result.n = originalData.n;
        result.a = originalData.a;
      }
    },
    // id not to change
    id: (
      result: Partial<Editor.Data.Structure.DocumentStyleData> = {},
      originalData: Editor.Data.Structure.DocumentStyleData,
      newData: Editor.Data.Structure.DocumentStyleData,
    ) => {
      result.id = originalData.id || newData.id;
    },
    // base not to change
    cs: (
      result: Partial<Editor.Data.Structure.DocumentStyleData> = {},
      originalData: Editor.Data.Structure.DocumentStyleData,
    ) => {
      if (originalData.cs != null) {
        result.cs = originalData.cs;
      }
    },
    // base not to change
    b: (
      result: Partial<Editor.Data.Structure.DocumentStyleData> = {},
      originalData: Editor.Data.Structure.DocumentStyleData,
    ) => {
      result.b = !!originalData.b;
    },
    // extend
    e: (
      result: Partial<Editor.Data.Structure.DocumentStyleData> = {},
      originalData: Editor.Data.Structure.DocumentStyleData,
      newData: Editor.Data.Structure.DocumentStyleData,
    ) => {
      if (newData.e != null) {
        result.e = newData.e;
      } else {
        result.e = originalData.e;
      }
    },
    // styles object
    p: (
      result: Partial<Editor.Data.Structure.DocumentStyleData> = {},
      originalData: Editor.Data.Structure.DocumentStyleData,
      newData: Editor.Data.Structure.DocumentStyleData,
    ) => {
      result.p = { ...originalData.p };

      if (newData.p != null) {
        const styleKeys = Object.typedKeys(newData.p);
        let i;
        for (i = 0; i < styleKeys.length; i++) {
          switch (styleKeys[i]) {
            //@ts-expect-error
            case 'fontFamily':
            case 'fontfamily':
              result.p.fontfamily = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'fontSize':
            case 'fontsize':
              result.p.fontsize = newData.p[styleKeys[i]];
              break;
            case 'color':
              result.p.color = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'backgroundColor':
              result.p.bg = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'backgroundcolor':
            case 'bg':
              result.p.bg = newData.p[styleKeys[i]];
              break;
            case 'bold':
              result.p.bold = newData.p[styleKeys[i]];
              break;
            case 'italic':
              result.p.italic = newData.p[styleKeys[i]];
              break;
            case 'underline':
              result.p.underline = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'alignment':
            case 'a':
              result.p.a = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'lineHeight':
            case 'lh':
              result.p.lh = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'lineRule':
            case 'lr':
              result.p.lr = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'spaceBefore':
            case 'sb':
              result.p.sb = newData.p[styleKeys[i]];
              break;
            //@ts-expect-error
            case 'spaceAfter':
            case 'sa':
              result.p.sa = newData.p[styleKeys[i]];
              break;
            case 'asa':
              result.p.asa = newData.p[styleKeys[i]];
              break;
            case 'asb':
              result.p.asb = newData.p[styleKeys[i]];
              break;
            case 'v':
              result.p.v = newData.p[styleKeys[i]];
              break;
            case 'ind':
              if (newData.p.ind?.l != null || newData.p.ind?.r != null) {
                if (result.p.ind == null) {
                  result.p.ind = {};
                }

                if (newData.p.ind?.l != null) {
                  result.p.ind.l = newData.p.ind.l;
                }

                if (newData.p.ind?.r != null) {
                  result.p.ind.r = newData.p.ind.r;
                }
              }
              break;
            case 'sp_ind':
              if (newData.p.sp_ind?.t != null) {
                result.p.sp_ind = {
                  t: newData.p.sp_ind.t,
                  v: Number(newData.p.sp_ind.v) || 0,
                };
              }
              break;
            case 'lst':
              if (newData.p.lst) {
                result.p.lst = {
                  lId: newData.p.lst.lId,
                  lLv: Number(newData.p.lst.lLv) || 0,
                };
              } else if (newData.p.lst === null) {
                // delete result.p.lst;
                result.p.lst = {
                  lId: null,
                  lLv: 0,
                };
              }
              break;
            case 'wc':
              result.p.wc = newData.p[styleKeys[i]];
              break;
            case 'kn':
              result.p.kn = newData.p[styleKeys[i]];
              break;
            case 'kl':
              result.p.kl = newData.p[styleKeys[i]];
              break;
            case 'cts':
              result.p.cts = newData.p[styleKeys[i]];
              break;
            case 'pbb':
              result.p.pbb = newData.p[styleKeys[i]];
              break;
            case 'otl':
              result.p.otl = newData.p[styleKeys[i]];
              break;
            default:
              break;
          }
        }
      }
    },
  };

  static validateName(style: Partial<Editor.Data.Structure.DocumentStyleData>, name: string) {
    if (style && style.n != null) {
      let _name = name;
      if (!_name) {
        _name = `${style.n}${style.a ? `;${style.a}` : ''}`;
      }

      const rawElements = _name.split(new RegExp('[,;]', 'g')).map((element) => element.trim());
      if (style.id && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 't'].includes(style.id)) {
        if (rawElements[0] === style.n) {
          style.a = rawElements.slice(1, rawElements.length).join(',');
        } else {
          style.a = rawElements.join(',');
        }
      } else {
        style.n = rawElements[0];
        style.a = rawElements.slice(1, rawElements.length).join(',');
      }
    }
    return style;
  }

  static parseBack(
    originalStyle: Partial<Editor.Data.Structure.DocumentStyleData>,
    styleToParse: any,
  ) {
    const result: any = {};

    const mapperKeys = Object.keys(DocumentStyle.DATA_MAPPER);

    let i;
    for (i = 0; i < mapperKeys.length; i++) {
      DocumentStyle.DATA_MAPPER[mapperKeys[i]](result, originalStyle, styleToParse);
    }
    return result;
  }

  static populateProperties(
    newProperties: Editor.Data.Structure.DocumentStyleProperties,
  ): Editor.Data.Structure.DocumentStyleProperties {
    return { ...DEFAULT_STYLE_OBJECT.p.p, ...newProperties };
  }

  static generateStyleId() {
    return `${Math.random().toString(36).substring(4)}`;
  }

  constructor(
    id: string,
    templateStyle: Partial<Editor.Data.Structure.DocumentStyleData> | null,
    documentStyle: Partial<Editor.Data.Structure.DocumentStyleData> | null,
  ) {
    super();
    this.id = id;
    if (templateStyle) {
      this.templateStyle = cloneDeep(templateStyle);
    }
    if (documentStyle) {
      this.documentStyle = cloneDeep(documentStyle);
    }
    this.joinStyleData();
  }

  get data() {
    return cloneDeep(this.mergedStyle);
  }

  get n() {
    return this.mergedStyle.n;
  }

  get a() {
    return this.mergedStyle.a;
  }

  get cs() {
    return this.mergedStyle.cs;
  }

  get b() {
    return this.mergedStyle.b;
  }

  get e() {
    return this.mergedStyle.e;
  }

  get p() {
    return this.mergedStyle.p;
  }

  get t() {
    return this.mergedStyle.t;
  }

  get tabs() {
    return this.mergedStyle?.p?.tabs || [];
  }

  get templateOnly() {
    return this.templateStyle && !this.documentStyle;
  }

  get hasDocumentDefinition() {
    return !!this.documentStyle;
  }

  get hasTemplateDefinition() {
    return !!this.templateStyle;
  }

  get originalP() {
    return this.documentStyle;
  }

  get extends() {
    return this.mergedStyle.e || null;
  }

  get extendedP() {
    return this.mergedStyle.extendedP;
  }

  get hasList() {
    return this.mergedStyle.p?.lst && this.mergedStyle.p?.lst.lId != null;
  }

  get styleDefinedListId() {
    return this.mergedStyle.p?.lst?.lId || null;
  }

  get outlineValue() {
    return this.mergedStyle.extendedP?.otl !== undefined ? this.mergedStyle.extendedP.otl : null;
  }

  private extendFromParentStyleProperties(
    parentProperties: Editor.Data.Structure.DocumentStyleProperties,
    styleProperties: Editor.Data.Structure.DocumentStyleProperties = this.mergedStyle.p || {},
  ) {
    const extended: Editor.Data.Structure.DocumentStyleProperties = {};
    merge(extended, parentProperties, styleProperties);
    if (extended) {
      extended.tabs = this.joinTabStops({ p: parentProperties }, { p: styleProperties });
    }
    return extended;
  }

  private joinTabStops(...args: Partial<Editor.Data.Structure.DocumentStyleData>[]) {
    let data: Editor.Data.TabStop[][] = [];
    for (let index = 0; index < args.length; index++) {
      data.push(args[index].p?.tabs || []);
    }
    return TabulationUtils.mergeTabStops(...data);
  }

  private joinStyleData() {
    let merged: Partial<MergedStyleData> = {};
    if (this.id === 'p' || !this.extends) {
      merge(merged, DEFAULT_STYLE_OBJECT.p, this.templateStyle || {}, this.documentStyle || {});
      if (merged.p) {
        merged.p.tabs = this.joinTabStops(
          DEFAULT_STYLE_OBJECT.p,
          this.templateStyle || {},
          this.documentStyle || {},
        );
      }
    } else {
      merge(merged, this.templateStyle || {}, this.documentStyle || {});
      if (merged.p) {
        merged.p.tabs = this.joinTabStops(this.templateStyle || {}, this.documentStyle || {});
      }
    }
    merged.extendedP = this.extendFromParentStyleProperties(this.parentProperties, merged.p);
    this.updateMergedData(merged);
  }

  private updateMergedData(newData: Partial<MergedStyleData>) {
    if (!isEqual(this.mergedStyle, newData)) {
      this.mergedStyle = newData;
      this.emit('UPDATED', this.mergedStyle);
      for (let index = 0; index < this.children.length; index++) {
        this.children[index].loadNewParentProperties(this.mergedStyle.extendedP);
      }
    }
  }

  loadStyleData(
    templateStyle: Partial<Editor.Data.Structure.DocumentStyleData> | null,
    documentStyle: Partial<Editor.Data.Structure.DocumentStyleData> | null,
  ) {
    this.resetParentProperties();

    let updated = false;

    if (!isEqual(this.templateStyle, templateStyle)) {
      this.templateStyle = cloneDeep(templateStyle);
      updated = true;
    }

    if (!isEqual(this.documentStyle, documentStyle)) {
      this.documentStyle = cloneDeep(documentStyle);
      updated = true;
    }

    if (updated) {
      this.joinStyleData();
    }

    return this;
  }

  resetParentProperties() {
    this.parentProperties = {};
  }

  loadNewParentProperties(
    parentProperties?: Editor.Data.Structure.DocumentStyleProperties,
    shouldUpdateChildren: boolean = true,
  ) {
    if (!isEqual(this.parentProperties, parentProperties)) {
      this.parentProperties = cloneDeep(parentProperties || {});
      let extendedP = this.extendFromParentStyleProperties(this.parentProperties);
      if (!isEqual(this.mergedStyle.extendedP, extendedP)) {
        this.mergedStyle.extendedP = extendedP;
        this.emit('UPDATED', this.mergedStyle);
        if (shouldUpdateChildren) {
          for (let index = 0; index < this.children.length; index++) {
            this.children[index].loadNewParentProperties(this.mergedStyle.extendedP);
          }
        }
      }
    }
    return this;
  }
}
